import { editDistance, fuzzyTolerance, isFuzzyMatch } from './fuzzy';
import { tokenize } from './text';

// Structural subset of Prisma's KnowledgeEntry — the retrieval module never
// imports the client, so it stays testable with plain object fixtures.
// links is `unknown` (not ChatLink[]) because it's raw stored Json on the
// Prisma side — scripted-chat-responder.ts validates its shape defensively
// when reading it, same as any other untrusted stored data.
export interface IndexableEntry {
  id: string;
  question: string;
  answer: string;
  keywords: string[];
  sortOrder: number;
  links?: unknown;
}

export interface Suggestion {
  entryId: string;
  question: string;
}

export interface ScoredEntry {
  entry: IndexableEntry;
  score: number;
  confidence: number;
}

// Keywords are the author's deliberate statement of what an entry is *for*;
// the question is descriptive prose that happens to be about it. Both are
// signal, but not equally.
const KEYWORD_WEIGHT = 1;
const QUESTION_WEIGHT = 0.5;

// A typo match counts, but must never outrank an entry that matched the same
// word spelled correctly.
const FUZZY_PENALTY = 0.6;

// See resolveToken: how hard a word the knowledge base has never seen drags
// confidence down.
const UNKNOWN_TERM_DAMPING = 0.5;

// Tunable in one place, and asserted against directly in the spec.
export const CONFIDENT_THRESHOLD = 0.45;
export const UNCERTAIN_THRESHOLD = 0.18;
export const AMBIGUITY_RATIO = 0.85;

interface IndexedEntry {
  entry: IndexableEntry;
  terms: Map<string, number>;
}

export interface KnowledgeIndex {
  entries: IndexedEntry[];
  idf: Map<string, number>;
  unknownIdf: number;
}

interface ResolvedTerm {
  term: string;
  idf: number;
  exact: boolean;
}

export function buildIndex(entries: IndexableEntry[]): KnowledgeIndex {
  const indexed: IndexedEntry[] = entries.map((entry) => {
    const terms = new Map<string, number>();

    for (const term of entry.keywords.flatMap(tokenize)) {
      terms.set(term, KEYWORD_WEIGHT);
    }

    // Free signal the old scorer threw away entirely — it only ever looked at
    // `keywords`, so an entry titled "What is your return policy?" got no
    // credit for the word "policy".
    for (const term of tokenize(entry.question)) {
      if (!terms.has(term)) terms.set(term, QUESTION_WEIGHT);
    }

    return { entry, terms };
  });

  const documentFrequency = new Map<string, number>();
  for (const { terms } of indexed) {
    for (const term of terms.keys()) {
      documentFrequency.set(term, (documentFrequency.get(term) ?? 0) + 1);
    }
  }

  // Inverse document frequency: a word carries signal in proportion to how
  // *few* entries use it. This is the whole point of the rewrite. In the
  // seeded FAQ "order" appears in four of six entries and is nearly
  // meaningless, while "shipping" appears in one and is decisive — the old
  // scorer counted both as exactly 1 and let ties fall to sortOrder, which is
  // why "how much is shipping on a ₹50 order" answered with order tracking.
  const total = indexed.length;
  const idf = new Map<string, number>();
  for (const [term, frequency] of documentFrequency) {
    idf.set(term, Math.log(1 + total / (1 + frequency)));
  }

  return {
    entries: indexed,
    idf,
    unknownIdf: Math.log(1 + total) * UNKNOWN_TERM_DAMPING,
  };
}

export function scoreQuery(index: KnowledgeIndex, message: string): ScoredEntry[] {
  const queryTerms = resolveQuery(index, message);
  if (queryTerms.length === 0) return [];

  const totalIdf = queryTerms.reduce((sum, term) => sum + term.idf, 0);
  if (totalIdf === 0) return [];

  const ranked: ScoredEntry[] = [];

  for (const { entry, terms } of index.entries) {
    let score = 0;

    for (const queryTerm of queryTerms) {
      const weight = terms.get(queryTerm.term);
      if (weight === undefined) continue;

      score += queryTerm.idf * weight * (queryTerm.exact ? 1 : FUZZY_PENALTY);
    }

    if (score > 0) {
      // Normalising by the query's own total IDF — rather than reporting a raw
      // count — is what kills the old verbosity bias, where an entry with 20
      // keywords beat a precise 2-of-2 match purely on surface area. It also
      // makes the number mean something transferable: "how much of what you
      // asked can this entry actually explain", comparable across entries and
      // across tenants, which is what makes fixed thresholds defensible.
      ranked.push({ entry, score, confidence: score / totalIdf });
    }
  }

  return ranked.sort((a, b) => b.score - a.score || a.entry.sortOrder - b.entry.sortOrder);
}

// Two entries scoring within a hair of each other means the query genuinely
// didn't discriminate between them. Answering the marginal winner with full
// confidence is how you get a wrong answer delivered plausibly; better to
// answer and show the alternative.
export function isAmbiguous(ranked: ScoredEntry[]): boolean {
  return ranked.length > 1 && ranked[1].score >= ranked[0].score * AMBIGUITY_RATIO;
}

export function toSuggestion(entry: IndexableEntry): Suggestion {
  return { entryId: entry.id, question: entry.question };
}

// When nothing matched there's no ranking to draw on, so fall back to the
// author's own priority ordering — "here's what I can help with" beats a dead
// end.
export function fallbackSuggestions(index: KnowledgeIndex, limit: number): Suggestion[] {
  return [...index.entries]
    .sort((a, b) => a.entry.sortOrder - b.entry.sortOrder)
    .slice(0, limit)
    .map(({ entry }) => toSuggestion(entry));
}

// Query words are resolved against the vocabulary *once*, before scoring, so
// every term has a stable IDF regardless of which entry is being scored. The
// alternative — resolving per entry — would let the numerator and denominator
// disagree about what a word is worth.
function resolveQuery(index: KnowledgeIndex, message: string): ResolvedTerm[] {
  const resolved = new Map<string, ResolvedTerm>();

  for (const token of new Set(tokenize(message))) {
    const candidate = resolveToken(index, token);
    const existing = resolved.get(candidate.term);

    // Two query words can land on the same vocabulary term ("ordr" and
    // "orders" both resolve to "order"). Count it once, and let an exact hit
    // displace a fuzzy one so the typo penalty isn't charged for a word the
    // shopper actually spelled correctly.
    if (!existing || (!existing.exact && candidate.exact)) {
      resolved.set(candidate.term, candidate);
    }
  }

  return [...resolved.values()];
}

function resolveToken(index: KnowledgeIndex, token: string): ResolvedTerm {
  const exactIdf = index.idf.get(token);
  if (exactIdf !== undefined) return { term: token, idf: exactIdf, exact: true };

  const near = nearestTerm(index, token);
  if (near !== null) return { term: near, idf: index.idf.get(near) as number, exact: false };

  // A word the knowledge base has never seen, so no entry can match it. It
  // still belongs in the denominator: it's signal this FAQ demonstrably can't
  // explain, and confidence should reflect that. Damped rather than counted at
  // full weight so that one unrecognised word — "₹50", a product name — hedges
  // an otherwise strong answer instead of burying it.
  return { term: token, idf: index.unknownIdf, exact: true };
}

function nearestTerm(index: KnowledgeIndex, token: string): string | null {
  let best: string | null = null;
  let bestDistance = Infinity;

  for (const term of index.idf.keys()) {
    if (!isFuzzyMatch(token, term)) continue;

    const distance = editDistance(token, term, fuzzyTolerance(term));
    if (distance < bestDistance) {
      best = term;
      bestDistance = distance;
    }
  }

  return best;
}
